import { Component, input, output, OnInit, ElementRef, AfterViewInit, viewChild, signal, effect, inject } from '@angular/core';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Project } from 'src/app/entities/project/project';
import { DateService } from 'src/app/services/date-service/date.service';
import { Tag } from 'src/app/entities/tag/tag';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { User } from 'src/app/entities/user/user';
import { skip } from 'rxjs';
import { CustomMenuComponent } from '../../atoms/custom-menu/custom-menu.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent, ConfirmationDialogData, ConfirmationDialogResult } from '../../atoms/confirmation-dialog/confirmation-dialog.component';
import { TimeTrackRepository } from 'src/app/services/remote-data-service/repositories/time-track-repository/time-track.repository';
import { StoreService } from 'src/app/services/store-service/store.service';
import { Uuid } from 'src/app/entities/value-objects/uuid/uuid';


/**
 * Enum defining available menu actions for a track entry.
 */
export enum trackEntryMenu {
  delete = 'Delete',
  duplicate = 'Duplicate',
  resume = 'Resume'
}


/**
 * Component that displays a modifiable time track.
 */
@Component({
  selector: 'app-track-entry',
  templateUrl: './track-entry.component.html',
  styleUrls: ['./track-entry.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTimepickerModule,
    CustomMenuComponent
  ]
})
export class TrackEntryComponent implements OnInit, AfterViewInit {
  /** The time track entry being edited */
  trackEntry = input.required<TimeTrack>();
  /** List of available projects to associate with the time track */
  projects = input<Project[]>([]);
  /** List of available tags to associate with the time track */
  tags = input<Tag[]>([]);
  /** List of users for author association */
  users = input<User[]>([]);

  /** Controls visibility of user selection UI */
  showUser = input(false);
  /** Currently selected author of the time track */
  author?: User;

  /** Signal holding the start time of the time track */
  startTime = signal(new Date());
  /** Signal holding the end time of the time track */
  endTime = signal(new Date());

  // Form group holding all track entry inputs
  /** Form group containing all track entry controls with validation */
  protected form = new FormGroup({
    /** Required title input control */
    title: new FormControl('', Validators.required),
    /** Project selection control */
    selectedProject: new FormControl<string | undefined>(undefined),
    /** Tag selection control */
    selectedTags: new FormControl<string[]>([]),
    /** Start date control initialized with current time */
    startDate: new FormControl(this.startTime(), Validators.required),
    /** Start time control initialized with current time */
    startTime: new FormControl(this.startTime(), Validators.required),
    /** End date control initialized with current time */
    endDate: new FormControl(this.endTime(), Validators.required),
    /** End time control initialized with current time */
    endTime: new FormControl(this.endTime(), Validators.required)
  });

  /**
   * Array of available menu items for track entry actions
   */
  menuItems: trackEntryMenu[] = [
    trackEntryMenu.delete,
    trackEntryMenu.duplicate,
    trackEntryMenu.resume
  ]

  /** Event emitter for delete actions */
  delete = output<TimeTrack>();
  /** Event emitter for update actions */
  updateEntry = output<TimeTrack>();
  /** Event emitter for duplicate actions */
  duplicateEntry = output<TimeTrack>();

  nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

  // Effect to sync author data when users input changes
  setAuthorEffect = effect(() => {
    this.users().find(user => {
      if (user.id === this.trackEntry().author?.id) {
        this.author = user;
      }
    });
  });

  constructor(
    protected dateService: DateService,
    protected timeTrackRep: TimeTrackRepository,
    protected store: StoreService
  ) { }

  ngAfterViewInit(): void {
    const trackEntry = this.trackEntry();
    this.adjustTitleWidth(trackEntry.title);
  }

  /**
   * Angular lifecycle hook - initializes component
   * Sets up form controls with initial values and validation
   * Configures value change subscriptions
   */
  ngOnInit(): void {
    const trackEntry = this.trackEntry();
    const allProjects = this.projects();
    const updatable = trackEntry.permissions.update;
    const inputs = ['title', 'selectedProject', 'selectedTags', 'user',
      'startDate', 'startTime', 'endDate', 'endTime'];

    if (!updatable) {
      for (const i of inputs) {
        this.form.get(i)?.disable()
      }
    }

    this.form.get('title')?.valueChanges.subscribe(text => {
      if (text !== null) this.adjustTitleWidth(text);
    });

    this.startTime.set(new Date(trackEntry.from));
    this.endTime.set(new Date(trackEntry.to || new Date()));

    const defaultProject = allProjects.find((project) => {
      return project.id === trackEntry.project?.id
    });


    this.form.patchValue({
      title: trackEntry.title,
      selectedProject: defaultProject?.id,
      selectedTags: trackEntry.tags.map(tag => tag.id),
      //user: defaultUser?.id,
      startDate: this.startTime(),
      startTime: this.startTime(),
      endDate: this.endTime(),
      endTime: this.endTime()
    }, { emitEvent: false })

    // Handle form changes to update track entry
    this.form.valueChanges.pipe(
      skip(2) // Remove skip or set to 0 to allow caching bypass by updated.
    ).subscribe((formChange) => {
      console.log('here');
      const startDate = formChange.startDate;
      const startTime = formChange.startTime;
      const endDate = formChange.endDate;
      const endTime = formChange.endTime;

      if (startDate && startTime && endDate && endTime && trackEntry.permissions.update) {
        const start = new Date(startDate);
        const startTimeDate = new Date(startTime);
        start.setHours(startTimeDate.getHours(), startTimeDate.getMinutes());
        this.startTime.set(start);

        const end = new Date(endDate);
        const endTimeDate = new Date(endTime);
        end.setHours(endTimeDate.getHours(), endTimeDate.getMinutes());
        this.endTime.set(end);

        const tags: Tag[] = [];

        formChange.selectedTags?.forEach(tagId => {
          const tag = this.tags().find(tag => tag.id === tagId);
          if (tag) {
            tags.push(tag);
          }
        });

        this.changeEntry(
          formChange.title || '',
          start,
          end,
          allProjects.find((project) => project.id === formChange.selectedProject),
          tags
        );
      }
    });

  }


  // Adjust title input width based on content using temporary span measurement
  /**
   * Adjusts the title input width based on content length
   * @param text - The current text content of the title input
   * @returns void
   */
  private adjustTitleWidth(text: string): void {
    const input = this.nameInput();

    if (!input) return;

    const span = document.createElement('span');
    span.style.visibility = 'hidden';
    span.style.position = 'absolute';
    span.style.whiteSpace = 'nowrap';
    const inputStyles = window.getComputedStyle(input.nativeElement);
    span.style.font = inputStyles.font;
    span.innerText = text;

    document.body.appendChild(span);
    const width = span.getBoundingClientRect().width;
    document.body.removeChild(span);

    const titleField = input.nativeElement.closest('.track-entry__title') as HTMLElement;
    if (titleField) {
      titleField.style.width = `${width + 32}px`;
    }
  }


  /**
   * Duplicates a time track entry
   *
   * Creates a new TimeTrack instance with same data
   * Emits duplicate event with the new track
   * @param track - The TimeTrack object to duplicate
   * @returns void
   */
  public duplicateTrack(track: TimeTrack): void {
    const duplicatedTrack = new TimeTrack(
      Uuid.generateId(),
      track.title,
      track.from,
      track.to,
      track.permissions,
      track.project,
      track.author,
      track.tags
    )

    this.duplicateEntry.emit(duplicatedTrack);
  }


  /**
   * Handles actions triggered from the custom menu
   *
   * @param selectedItem - The menu item selected
   */
  operateOverSelectedMenuItem(selectedItem: trackEntryMenu): void {
    switch (selectedItem) {
      case trackEntryMenu.delete: {
        const dialog = this.openConfirmationDialog('0ms', '0ms');

        dialog.afterClosed().subscribe((res: ConfirmationDialogResult) => {
          if (res === ConfirmationDialogResult.Confirm) {
            this.deleteTrackEntry()
          }
        });

        break;
      }
      case trackEntryMenu.duplicate:
        this.duplicateTrack(this.trackEntry());
        break;
      case trackEntryMenu.resume: {
        const newTrackByResumer = new TimeTrack(
          Uuid.generateId(),
          this.trackEntry().title,
          new Date(),
          null,
          this.trackEntry().permissions,
          this.trackEntry().project,
          this.trackEntry().author,
          this.trackEntry().tags,
          this.trackEntry().time_spent
        )


        this.store.startTracking(newTrackByResumer);
        break;
      }
      default:
        break;
    }
  }


  // DI of MatDialog for opening modal
  readonly dialog = inject(MatDialog);


  /**
   * Configuration data for the confirmation dialog
   */
  confirmationDialogInputs: ConfirmationDialogData = {
    title: 'Delete Track',
    content: 'Are you sure about to delete this tracking?',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No'
  }


  /**
   * Opens a confirmation dialog for delete action
   *
   * @param enterAnimationDuration - Animation duration for dialog opening
   * @param exitAnimationDuration - Animation duration for dialog closing
   * @returns Reference to the opened dialog
   */
  openConfirmationDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ) {
    return this.dialog.open(ConfirmationDialogComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: this.confirmationDialogInputs
    });
  }


  /**
   * Gets the property keys of a TimeTrack entry
   * @param entry - The TimeTrack object to inspect
   * @returns Array of property keys as strings
   */
  getItemKeys(entry: TimeTrack): string[] {
    return Object.keys(entry);
  }

  /**
   * Formats a Date object to time string (HH:MM)
   * @param time - Date object to format
   * @returns Formatted time string
   */
  formatTime(time: Date): string {
    return new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  /**
   * Formats a Date object to localized date string
   * @param date - Date object to format
   * @returns Formatted date string in 'es-ES' locale
   */
  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Formats time display with 12-hour clock and AM/PM
   * @param date - Date object to format
   * @returns Formatted time string with AM/PM indicator
   */
  formatTimeDisplay(date: Date): string {
    return new Date(date).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  /**
   * Converts milliseconds duration to HH:MM:SS format
   * @param duration - Duration in milliseconds
   * @returns Formatted time string (HH:MM:SS)
   */
  formatDuration(duration: number): string {
    const hours = Math.floor(duration / 3600000);
    const minutes = Math.floor((duration % 3600000) / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);

    const hoursString = String(hours).padStart(2, '0');
    const minutesString = String(minutes).padStart(2, '0');
    const secondsString = String(seconds).padStart(2, '0');

    return `${hoursString}:${minutesString}:${secondsString}`;
  }

  /**
   * Handles track entry deletion
   * Emits delete event with current track entry
   * @returns void
   */
  deleteTrackEntry(): void {
    this.delete.emit(this.trackEntry());
  }

  /**
   * Handles track entry updates
   * @param name - New title for the time track
   * @param start - New start time Date
   * @param end - New end time Date
   * @param project - Associated Project or undefined
   * @param tags - Array of associated Tags
   * @returns void
   */
  changeEntry(name: string, start: Date, end: Date, project: Project | undefined, tags: Tag[]): void {
    const trackEntry = this.trackEntry();
    const newTrackEntry = new TimeTrack(
      trackEntry.id,
      name,
      start,
      end,
      {
        update: false,
        delete: false
      },
      project,
      undefined,
      tags,
    )

    this.updateEntry.emit(newTrackEntry)
  }
}
